import { AllSelection, TextSelection } from '@atlaskit/editor-prosemirror/state';
import { fg } from '@atlaskit/platform-feature-flags/fg';

import type {
	ReplaceDocCommand,
	StreamingCommandFactory,
} from '../aiStreamingOrchestratorPluginType';

/**
 * Creates a command factory for `replaceDoc` — replaces the entire document
 * with streamed HTML content. Sets a whole-document selection so the streaming
 * pipeline pastes the new content in place, identical to how `replaceNode`
 * sets a node-scoped selection before streaming begins.
 *
 * Notes:
 * - No localId is read or required (the doc node has none).
 * - An empty value is intentionally allowed: when the tool call returns empty
 *   content the document will be cleared (e.g. "delete entire page" intent).
 *
 * CCI-16841: When the original document begins with a wrapper node (e.g. a
 * panel) and the streamed replacement does not, `TextSelection.between` snaps
 * the selection inward to the nearest text positions — leaving the wrapper's
 * boundaries outside the streaming range. Subsequent `replaceRange` calls
 * then merge new top-level nodes (heading, paragraph, etc.) into the
 * preserved wrapper, producing duplicated panels and other "split" artefacts.
 * `AllSelection` keeps `from = 0` and `to = doc.content.size`, so the
 * streaming pipeline replaces from the document boundary cleanly.
 */
export const createReplaceDocCommand: StreamingCommandFactory<ReplaceDocCommand> =
	(api) => (_command) => {
		return api.core.actions.execute(({ tr }) => {
			if (fg('platform_editor_ai_replace_doc_use_all_selection')) {
				return tr.setSelection(new AllSelection(tr.doc)).scrollIntoView();
			}
			const $start = tr.doc.resolve(0);
			const $end = tr.doc.resolve(tr.doc.content.size);
			return tr.setSelection(TextSelection.between($start, $end)).scrollIntoView();
		});
	};
